import type { SalonInvoiceProfile } from '../../services/peInvoiceFirebaseService';
import type {
  InvoiceCalculations,
  InvoiceCustomer,
  InvoiceItem,
  InvoiceMeta,
  InvoiceShipping,
} from '../../store/usePEInvoiceStore';
import type { CutierInvoiceProfile } from '../../types/cutierInvoiceProfile';
import { isValidIban } from '../pe-papers/ibanValidation';

export interface CutierInvoiceCompleteness {
  complete: boolean;
  eInvoiceReady: boolean;
  missing: string[];
}

const hasText = (v: unknown) => String(v ?? '').trim().length > 0;

export function assessCutierInvoiceProfile(profile: CutierInvoiceProfile): CutierInvoiceCompleteness {
  const missing: string[] = [];

  if (!hasText(profile.displayName)) missing.push('Firmenname (Aussteller)');
  if (!hasText(profile.street)) missing.push('Straße (Aussteller)');
  if (!hasText(profile.zip)) missing.push('PLZ (Aussteller)');
  if (!hasText(profile.city)) missing.push('Ort (Aussteller)');
  if (!hasText(profile.email)) missing.push('E-Mail (Aussteller)');
  if (!hasText(profile.accountHolder)) missing.push('Kontoinhaber');
  if (!isValidIban(profile.iban)) missing.push('Gültige IBAN');
  if (!hasText(profile.taxNumber) && !hasText(profile.vatId)) {
    missing.push('Steuernummer oder USt-IdNr.');
  }

  if (!hasText(profile.buyerCompanyName)) missing.push('Firma (Empfänger)');
  if (!hasText(profile.buyerContactPerson)) missing.push('Ansprechpartner (Empfänger)');
  if (!hasText(profile.buyerEmail)) missing.push('E-Mail (Empfänger)');
  if (!hasText(profile.buyerStreet)) missing.push('Straße (Empfänger)');
  if (!hasText(profile.buyerZip)) missing.push('PLZ (Empfänger)');
  if (!hasText(profile.buyerCity)) missing.push('Ort (Empfänger)');

  const complete = missing.length === 0;
  return {
    complete,
    eInvoiceReady: complete,
    missing,
  };
}

export function toSalonInvoiceProfile(profile: CutierInvoiceProfile): SalonInvoiceProfile {
  const displayName = String(profile.displayName || '').trim();
  const accountHolder = String(profile.accountHolder || displayName).trim();
  return {
    accountHolder,
    bankName: profile.bankName,
    iban: profile.iban,
    bic: profile.bic,
    taxNumber: profile.taxNumber,
    vatId: profile.vatId,
    invoicePrefix: 'RD-CUT-',
    defaultPaymentTermsDays: profile.defaultPaymentTermsDays || 14,
    senderCompanyName: displayName || accountHolder,
    senderStreet: profile.street,
    senderZip: profile.zip,
    senderCity: profile.city,
    senderCountry: profile.country || 'DE',
    senderEmail: profile.email,
    senderPhone: profile.phone,
  };
}

export function toInvoiceCustomer(profile: CutierInvoiceProfile): InvoiceCustomer {
  return {
    type: 'business',
    firstName: '',
    lastName: '',
    companyName: profile.buyerCompanyName,
    contactPerson: profile.buyerContactPerson,
    vatId: profile.buyerVatId,
    buyerReference: '',
    email: profile.buyerEmail,
    street: profile.buyerStreet,
    zip: profile.buyerZip,
    city: profile.buyerCity,
    country: profile.buyerCountry || 'DE',
  };
}

export function buildCutierZugferdPayload(opts: {
  profile: CutierInvoiceProfile;
  monthKey: string;
  invoiceNumber: string;
  invoiceDateIso: string;
  unitCount: number;
  unitGross: number;
  netTotal: number;
  grossTotal: number;
  vatTotal: number;
}): {
  profile: SalonInvoiceProfile;
  customer: InvoiceCustomer;
  items: InvoiceItem[];
  shipping: InvoiceShipping;
  meta: InvoiceMeta;
  calculations: InvoiceCalculations;
} {
  const unitNet = opts.unitGross / 1.19;
  return {
    profile: toSalonInvoiceProfile(opts.profile),
    customer: toInvoiceCustomer(opts.profile),
    items: [
      {
        id: 'cutier-1',
        kind: 'service',
        description: `Seminar-Vergütung Online-Schulung (${opts.monthKey})`,
        quantity: opts.unitCount,
        unit: 'Stk.',
        unitPrice: unitNet,
        priceMode: 'net',
        vatRate: 19,
        serviceHours: 0,
        hourlyRate: 0,
        materialMode: 'exclusive',
      },
    ],
    shipping: { enabled: false, amount: 0, vatRate: 19 },
    meta: {
      docType: 'invoice',
      invoiceNumber: opts.invoiceNumber,
      invoiceDate: opts.invoiceDateIso,
      deliveryDate: opts.invoiceDateIso,
      paymentDeadlineDays: opts.profile.defaultPaymentTermsDays || 14,
      notes: '',
    },
    calculations: {
      netTotal: opts.netTotal,
      grossTotal: opts.grossTotal,
      vatTotal: opts.vatTotal,
      standardVatRate: 19,
      isVatExempt: false,
      vatBreakdown: {
        standard: { rate: 19, net: opts.netTotal, vat: opts.vatTotal },
      },
    },
  };
}

export function formatSenderLine(profile: CutierInvoiceProfile): string {
  const parts = [profile.displayName, profile.street, `${profile.zip} ${profile.city}`.trim(), profile.email]
    .map((p) => String(p || '').trim())
    .filter(Boolean);
  return parts.join(' · ');
}
